// src/lib/financialCalculation.ts
//
// BekalNanti — P2 Pure Deterministic Financial Calculations
// Strictly deterministic, pure mathematical functions. Zero external dependencies.
// Does NOT provide financial advice, make subjective judgments, or invent assumptions.

export interface IncomeItem {
  id?: string
  name: string
  amount: number
  currency?: string
  frequency: 'monthly' | 'weekly' | 'annual' | 'one_time' | string
  isActive?: boolean
  sourceType?: string
}

export interface ExpenseItem {
  id?: string
  name: string
  amount: number
  currency?: string
  frequency: 'monthly' | 'weekly' | 'annual' | string
  category?: string
  isEssential: boolean
}

export interface DebtItem {
  id?: string
  name: string
  outstandingAmount: number
  monthlyPayment: number
  currency?: string
  interestRate?: number | null
  remainingMonths?: number | null
  isActive?: boolean
}

export interface SavingItem {
  id?: string
  name: string
  balance: number
  currency?: string
  purpose?: string
  isAvailableForGoal: boolean
}

export interface ObligationItem {
  id?: string
  name: string
  amount: number
  currency?: string
  frequency: 'monthly' | 'weekly' | 'annual' | string
  category?: string
  isActive?: boolean
}

export interface FinancialSummary {
  totalMonthlyIncome: number
  totalMonthlyExpenses: number
  essentialMonthlyExpenses: number
  discretionaryMonthlyExpenses: number
  totalMonthlyDebtPayments: number
  totalMonthlyObligations: number
  totalMonthlyCommitments: number
  netMonthlyCapacity: number
  totalSavings: number
  availableSavings: number
  totalOutstandingDebt: number
}

/**
 * Convert any frequency amount to an equivalent monthly amount deterministically.
 */
export function toMonthlyAmount(amount: number, frequency: string = 'monthly'): number {
  if (!amount || isNaN(amount) || amount <= 0) return 0
  const normalizedFreq = (frequency || '').toLowerCase().trim()

  switch (normalizedFreq) {
    case 'weekly':
      // 52 weeks / 12 months = 4.3333... weeks per month
      return Math.round((amount * 52) / 12)
    case 'annual':
    case 'yearly':
      return Math.round(amount / 12)
    case 'one_time':
      // One-time amounts are balance-like or episodic, not recurring monthly cashflow
      return 0
    case 'monthly':
    default:
      return Math.round(amount)
  }
}

/**
 * Calculate total active monthly income.
 */
export function calculateTotalMonthlyIncome(incomes: IncomeItem[] = []): number {
  return incomes
    .filter((inc) => Boolean(inc && inc.isActive !== false))
    .reduce((sum, inc) => sum + toMonthlyAmount(inc.amount, inc.frequency), 0)
}

/**
 * Calculate total monthly expenses.
 */
export function calculateTotalMonthlyExpenses(expenses: ExpenseItem[] = []): number {
  return expenses
    .filter(Boolean)
    .reduce((sum, exp) => sum + toMonthlyAmount(exp.amount, exp.frequency), 0)
}

/**
 * Calculate essential monthly expenses (needs).
 */
export function calculateEssentialExpenses(expenses: ExpenseItem[] = []): number {
  return expenses
    .filter((exp) => Boolean(exp && exp.isEssential === true))
    .reduce((sum, exp) => sum + toMonthlyAmount(exp.amount, exp.frequency), 0)
}

/**
 * Calculate discretionary monthly expenses (wants).
 */
export function calculateDiscretionaryExpenses(expenses: ExpenseItem[] = []): number {
  return expenses
    .filter((exp) => Boolean(exp && exp.isEssential === false))
    .reduce((sum, exp) => sum + toMonthlyAmount(exp.amount, exp.frequency), 0)
}

/**
 * Calculate total monthly payments for active debts.
 */
export function calculateTotalMonthlyDebtPayments(debts: DebtItem[] = []): number {
  return debts
    .filter((d) => Boolean(d && d.isActive !== false))
    .reduce((sum, d) => sum + (d.monthlyPayment && d.monthlyPayment > 0 ? Math.round(d.monthlyPayment) : 0), 0)
}

/**
 * Calculate total outstanding principal/amount for active debts.
 */
export function calculateTotalOutstandingDebt(debts: DebtItem[] = []): number {
  return debts
    .filter((d) => Boolean(d && d.isActive !== false))
    .reduce((sum, d) => sum + (d.outstandingAmount && d.outstandingAmount > 0 ? Math.round(d.outstandingAmount) : 0), 0)
}

/**
 * Calculate total recurring monthly obligations (e.g., family support / sandwhich generation).
 */
export function calculateTotalMonthlyObligations(obligations: ObligationItem[] = []): number {
  return obligations
    .filter((ob) => Boolean(ob && ob.isActive !== false))
    .reduce((sum, ob) => sum + toMonthlyAmount(ob.amount, ob.frequency), 0)
}

/**
 * Calculate total fixed monthly commitments (debts + obligations).
 */
export function calculateTotalMonthlyCommitments(
  debts: DebtItem[] = [],
  obligations: ObligationItem[] = []
): number {
  return calculateTotalMonthlyDebtPayments(debts) + calculateTotalMonthlyObligations(obligations)
}

/**
 * Calculate Net Monthly Capacity (C_net).
 * C_net = Total Monthly Income - Total Monthly Expenses - Total Monthly Commitments
 */
export function calculateNetMonthlyCapacity(
  totalIncome: number,
  totalExpenses: number,
  totalCommitments: number
): number {
  return totalIncome - totalExpenses - totalCommitments
}

/**
 * Calculate total liquid / dedicated savings marked as available for the goal.
 */
export function calculateAvailableSavings(savings: SavingItem[] = []): number {
  return savings
    .filter((s) => Boolean(s && s.isAvailableForGoal === true))
    .reduce((sum, s) => sum + (s.balance && s.balance > 0 ? Math.round(s.balance) : 0), 0)
}

/**
 * Calculate total balance across all savings.
 */
export function calculateTotalSavings(savings: SavingItem[] = []): number {
  return savings
    .filter(Boolean)
    .reduce((sum, s) => sum + (s.balance && s.balance > 0 ? Math.round(s.balance) : 0), 0)
}

/**
 * Generate full deterministic financial summary.
 */
export function summarizeFinancialReality(data: {
  incomes?: IncomeItem[]
  expenses?: ExpenseItem[]
  debts?: DebtItem[]
  savings?: SavingItem[]
  obligations?: ObligationItem[]
}): FinancialSummary {
  const incomes = data.incomes || []
  const expenses = data.expenses || []
  const debts = data.debts || []
  const savings = data.savings || []
  const obligations = data.obligations || []

  const totalMonthlyIncome = calculateTotalMonthlyIncome(incomes)
  const totalMonthlyExpenses = calculateTotalMonthlyExpenses(expenses)
  const essentialMonthlyExpenses = calculateEssentialExpenses(expenses)
  const discretionaryMonthlyExpenses = calculateDiscretionaryExpenses(expenses)
  const totalMonthlyDebtPayments = calculateTotalMonthlyDebtPayments(debts)
  const totalMonthlyObligations = calculateTotalMonthlyObligations(obligations)
  const totalMonthlyCommitments = totalMonthlyDebtPayments + totalMonthlyObligations
  const netMonthlyCapacity = calculateNetMonthlyCapacity(
    totalMonthlyIncome,
    totalMonthlyExpenses,
    totalMonthlyCommitments
  )
  const availableSavings = calculateAvailableSavings(savings)
  const totalSavings = calculateTotalSavings(savings)
  const totalOutstandingDebt = calculateTotalOutstandingDebt(debts)

  return {
    totalMonthlyIncome,
    totalMonthlyExpenses,
    essentialMonthlyExpenses,
    discretionaryMonthlyExpenses,
    totalMonthlyDebtPayments,
    totalMonthlyObligations,
    totalMonthlyCommitments,
    netMonthlyCapacity,
    totalSavings,
    availableSavings,
    totalOutstandingDebt,
  }
}

/**
 * Format currency amount into Indonesian Rupiah format (e.g. Rp 15.000.000).
 */
export function formatIDR(amount: number): string {
  if (isNaN(amount) || amount === null || amount === undefined) {
    return 'Rp 0'
  }
  const isNegative = amount < 0
  const absVal = Math.abs(Math.round(amount))
  const formatted = new Intl.NumberFormat('id-ID').format(absVal)
  return isNegative ? `-Rp ${formatted}` : `Rp ${formatted}`
}

/**
 * Format compact IDR (e.g. Rp 15 Jt, Rp 1,2 M).
 */
export function formatCompactIDR(amount: number): string {
  if (isNaN(amount) || amount === null || amount === undefined) {
    return 'Rp 0'
  }
  const isNegative = amount < 0
  const absVal = Math.abs(amount)

  let formatted = ''
  if (absVal >= 1_000_000_000) {
    formatted = `${(absVal / 1_000_000_000).toFixed(1).replace('.0', '').replace('.', ',')} M`
  } else if (absVal >= 1_000_000) {
    formatted = `${(absVal / 1_000_000).toFixed(1).replace('.0', '').replace('.', ',')} Jt`
  } else if (absVal >= 1_000) {
    formatted = `${(absVal / 1_000).toFixed(0)} Rb`
  } else {
    formatted = `${absVal}`
  }

  return isNegative ? `-Rp ${formatted}` : `Rp ${formatted}`
}
